import { useOnClickOutside } from '@monofront/business/hooks';
import React, {
  AriaAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface IBaseTooltipProps {
  /**
   * Content that will always be shown and that will need to be clicked to
   * show the tooltip.
   */
  children: React.ReactNode;
  /**
   * Content displayed inside the Tooltip.
   */
  content: React.ReactNode;
  /**
   * Where the tooltip will be shown, relative to the tooltip target center.
   */
  position: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Arrow position relative of the center anchor of the tooltip.
   * For example, when the `position` is `bottom`, `west` `arrowPosition` will have
   * the arrow on the top-left of the tooltip. With `position` `left`, the arrow
   * will be on the bottom-left of the tooltip.
   */
  arrowPosition: 'center' | 'west' | 'east';
  /**
   * Whether the tooltip target is `block` or `inline`. (default is block)
   */
  inline?: boolean;
  ariaId: Required<AriaAttributes['aria-describedby']>;
  dataTestId?: string;
  /**
   * Additional className on the tooltip.
   */
  UNSAFE_className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

type TTooltipProps = IBaseTooltipProps &
  (
    | {
        /**
         * Which event to handle to show the tooltip.
         * `hover` and `focus` will be handled using css pseudo classes,
         * `click-only` is the `onClick` JS event.
         * In any case, the tooltip will always responds to click events to be mobile-compatible.
         */
        eventType: 'hover' | 'focus' | 'click-only';
        isOpen?: never;
      }
    | {
        /**
         * Which event to handle to show the tooltip.
         * `controlled` let parent control
         * In any case, the tooltip will always responds to click events to be mobile-compatible.
         */
        eventType: 'controlled';
        /**
         * used only with eventType `controlled` to let parent handling open/close
         */
        isOpen: boolean;
      }
  );

/**
 * Tooltips display additional information upon hover, focus or click (only for a rich tooltip).
 * The information included should be contextual, helpful, and nonessential, while providing, that extra ability to
 * communicate and give clarity to a user.
 */
const Tooltip = ({
  children,
  content,
  position,
  arrowPosition,
  eventType,
  inline = false,
  UNSAFE_className = '',
  ariaId,
  isOpen = false,
  dataTestId,
  onClick,
}: TTooltipProps) => {
  const [shown, setShown] = useState(false);
  const tooltipRef = useRef(null);
  const isControlledTooltip = eventType === 'controlled';
  const displayClass = inline ? 'tw-inline-block' : 'tw-block';
  const positionClass = `tw-lf-tooltip-${position}`;
  const arrowClass = `tw-lf-tooltip-arrow-${arrowPosition}`;
  const shownClass = shown ? ' tw-lf-tooltip-shown' : '';
  const eventClass =
    eventType === 'hover'
      ? ' hover:tw-lf-tooltip-shown'
      : eventType === 'focus'
      ? ' focus:tw-lf-tooltip-shown'
      : '';

  const toggleShown = useCallback(() => {
    setShown((shown) => !shown);
  }, []);

  const hide = useCallback(() => {
    setShown(false);
  }, []);

  useEffect(() => {
    if (!isControlledTooltip) {
      return;
    }

    setShown(isOpen);
  }, [isOpen]);

  useOnClickOutside(tooltipRef, hide);

  return (
    <span
      ref={tooltipRef}
      hidden={!shown}
      className={`tw-flex tw-flex-1 tw-lf-tooltip-target${eventClass}${shownClass} ${displayClass}`}
      onClick={(e) =>
        onClick ? onClick(e) : !isControlledTooltip && toggleShown()
      }
      aria-describedby={ariaId}
      tabIndex={0}
    >
      {children}
      {shown && (
        <span
          id={ariaId}
          role="tooltip"
          className={`tw-lf-tooltip ${positionClass} ${arrowClass} ${UNSAFE_className}`}
          tabIndex={-1}
          data-testid={dataTestId}
        >
          {content}
        </span>
      )}
    </span>
  );
};

export default Tooltip;
